import React, { useState, useEffect } from 'react';

// Componente de banderas usando imágenes SVG de flagcdn.com
// Las imágenes se cargan de una CDN confiable para mejor compatibilidad

// Mapeo de códigos de país a códigos ISO para la CDN
const countryCodeMap = {
  CL: 'cl',
  BO: 'bo',
  AR: 'ar',
  PE: 'pe',
  VE: 've',
  CO: 'co',
  MX: 'mx',
  BR: 'br',
  UY: 'uy',
  PY: 'py',
  EC: 'ec',
  US: 'us',
  ES: 'es',
};

// Emojis de banderas como fallback
const flagEmojis = {
  CL: '🇨🇱',
  BO: '🇧🇴',
  AR: '🇦🇷',
  PE: '🇵🇪',
  VE: '🇻🇪',
  CO: '🇨🇴',
  MX: '🇲🇽',
  BR: '🇧🇷',
  UY: '🇺🇾',
  PY: '🇵🇾',
  EC: '🇪🇨',
  US: '🇺🇸',
  ES: '🇪🇸',
};

// Tamaños de las banderas en pixels
const sizePixels = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 40,
  '2xl': 48,
};

// Mapeo de nombres de países y códigos de moneda a códigos de país
const countryMap = {
  'Chile': 'CL',
  'Bolivia': 'BO',
  'Argentina': 'AR',
  'Perú': 'PE',
  'Peru': 'PE',
  'Venezuela': 'VE',
  'Colombia': 'CO',
  'México': 'MX',
  'Mexico': 'MX',
  'Brasil': 'BR',
  'Brazil': 'BR',
  'Uruguay': 'UY',
  'Paraguay': 'PY',
  'Ecuador': 'EC',
  'Estados Unidos': 'US',
  'USA': 'US',
  'España': 'ES',
  'Spain': 'ES',
  'CL': 'CL',
  'BO': 'BO',
  'AR': 'AR',
  'PE': 'PE',
  'VE': 'VE',
  'CO': 'CO',
  'MX': 'MX',
  'BR': 'BR',
  'UY': 'UY',
  'PY': 'PY',
  'EC': 'EC',
  'US': 'US',
  'ES': 'ES',
  // Códigos de moneda
  'CLP': 'CL',
  'BOB': 'BO',
  'ARS': 'AR',
  'PEN': 'PE',
  'VES': 'VE',
  'COP': 'CO',
  'MXN': 'MX',
  'BRL': 'BR',
  'UYU': 'UY',
  'PYG': 'PY',
  'USD': 'US',
  'EUR': 'ES',
};

// Componente principal
const Flag = ({ country, size = 'md', className = '' }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  const code = countryMap[country] || country;
  const isoCode = countryCodeMap[code];
  const pixelSize = typeof size === 'number' ? size : (sizePixels[size] || sizePixels.md);

  // Fallback con emoji
  if (!isoCode || error) {
    const emoji = flagEmojis[code];
    if (emoji) {
      return (
        <span 
          className={`inline-flex items-center justify-center ${className}`}
          style={{ 
            fontSize: pixelSize * 0.9,
            lineHeight: 1
          }}
          role="img"
          aria-label={`Bandera ${country}`}
        >
          {emoji}
        </span>
      );
    }
    // Fallback con código del país si no hay emoji
    return (
      <span 
        className={`inline-flex items-center justify-center bg-gray-200 rounded ${className}`}
        style={{ 
          width: pixelSize, 
          height: pixelSize * 0.67,
          fontSize: pixelSize * 0.4,
          fontWeight: 'bold'
        }}
      >
        {code?.substring(0, 2) || '??'}
      </span>
    );
  }

  // Usar flagcdn.com para imágenes PNG de alta calidad
  const flagUrl = `https://flagcdn.com/w${Math.max(pixelSize * 2, 80)}/${isoCode}.png`;

  return (
    <>
      {!loaded && !error && (
        <span 
          className={`inline-flex items-center justify-center ${className}`}
          style={{ 
            fontSize: pixelSize * 0.9,
            lineHeight: 1
          }}
          role="img"
          aria-label={`Bandera ${country}`}
        >
          {flagEmojis[code] || '🏳️'}
        </span>
      )}
      <img 
        src={flagUrl} 
        alt=""
        className={`${className}`}
        style={{ 
          width: pixelSize, 
          height: 'auto',
          verticalAlign: 'middle',
          display: loaded ? 'inline-block' : 'none',
          borderRadius: '2px'
        }}
        loading="eager"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </>
  );
};

// Componente para mostrar bandera con nombre del país
export const FlagWithName = ({ country, size = 'md', showCurrency = false, currency = '' }) => {
  return (
    <div className="flex items-center gap-2">
      <Flag country={country} size={size} />
      <div>
        <span className="font-semibold">{country}</span>
        {showCurrency && currency && (
          <span className="text-gray-500 text-sm ml-1">({currency})</span>
        )}
      </div>
    </div>
  );
};

// Exportar banderas individuales como componentes
export const ChileFlag = ({ size = 'lg' }) => <Flag country="CL" size={size} />;
export const BoliviaFlag = ({ size = 'lg' }) => <Flag country="BO" size={size} />;
export const ArgentinaFlag = ({ size = 'lg' }) => <Flag country="AR" size={size} />;
export const PeruFlag = ({ size = 'lg' }) => <Flag country="PE" size={size} />;
export const VenezuelaFlag = ({ size = 'lg' }) => <Flag country="VE" size={size} />;
export const ColombiaFlag = ({ size = 'lg' }) => <Flag country="CO" size={size} />;

export default Flag;
